import React, { useState } from "react";
import {
  attachmentDownloadUrl,
  deleteInitiativeAttachment,
  initiativeErrorMessage,
  uploadInitiativeAttachments,
} from "../services/efficiencyInitiativesApi";
import FieldHelp from "../help/FieldHelp";

/**
 * Supporting evidence for one initiative — documents, photographs, invoices.
 *
 * Used in two places, which is why the picker is exported separately:
 *   * this component, in the initiative's detail modal, to view/add/remove
 *   * CompleteInitiativeDialog, which only needs to CHOOSE files (they are
 *     uploaded after the status transition succeeds, so there is nothing to
 *     upload against yet while the dialog is open)
 *
 * Evidence is optional everywhere. "No documents attached" is a normal state and
 * is worded as such rather than as a warning.
 */

// Matches ALLOWED_ATTACHMENT_EXTENSIONS in routers/efficiency_initiatives.py. The
// server is the authority — this only spares the user a round trip to be told no,
// and populates the file dialog's filter.
export const ACCEPTED_FILE_TYPES =
  ".pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,.doc,.docx,.xls,.xlsx,.csv,.txt";

export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_FILES_PER_UPLOAD = 10;

export function formatBytes(bytes) {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * File chooser with a running list of what was picked.
 *
 * Oversized files are rejected here rather than on the server: a 60 MB video would
 * otherwise be uploaded in full before being refused, which on an office uplink is
 * a minute of waiting for an error.
 */
export function FilePicker({ files, onChange, disabled, inputId = "ei-file-input" }) {
  const [rejected, setRejected] = useState([]);

  const addFiles = (picked) => {
    const incoming = Array.from(picked || []);
    const tooBig = incoming.filter((f) => f.size > MAX_FILE_BYTES);
    const ok = incoming.filter((f) => f.size <= MAX_FILE_BYTES);
    const problems = tooBig.map(
      (f) => `${f.name} is ${formatBytes(f.size)}, over the ${formatBytes(MAX_FILE_BYTES)} limit`
    );

    // Dedupe on name+size: picking twice from the dialog is easy to do by accident
    // and uploading the same invoice twice is never what was meant.
    const merged = [...files];
    ok.forEach((f) => {
      if (!merged.some((m) => m.name === f.name && m.size === f.size)) merged.push(f);
    });

    // Anything past the cap is DROPPED, so say which. Slicing quietly meant
    // picking twelve files attached ten, and the two that vanished were the two
    // the user was least likely to check for.
    const kept = merged.slice(0, MAX_FILES_PER_UPLOAD);
    const dropped = merged.slice(MAX_FILES_PER_UPLOAD);
    if (dropped.length > 0) {
      problems.push(
        `${MAX_FILES_PER_UPLOAD} files is the maximum per upload, so ` +
          `${dropped.map((f) => f.name).join(", ")} ` +
          `${dropped.length === 1 ? "was" : "were"} not added — attach them separately`
      );
    }

    setRejected(problems);
    onChange(kept);
  };

  return (
    <div className="ei-file-picker">
      <label className="ei-file-drop" htmlFor={inputId}>
        <input
          id={inputId}
          type="file"
          multiple
          accept={ACCEPTED_FILE_TYPES}
          disabled={disabled}
          onChange={(e) => {
            addFiles(e.target.files);
            // Reset so re-picking the same file fires another change event.
            e.target.value = "";
          }}
        />
        <span className="ei-file-drop-label">Choose files</span>
        <span className="ei-hint">
          PDF, images, Word, Excel, CSV · max {formatBytes(MAX_FILE_BYTES)}
        </span>
      </label>

      {rejected.length > 0 && (
        <ul className="ei-file-error">
          {rejected.map((msg, i) => (
            <li key={i}>{msg}</li>
          ))}
        </ul>
      )}

      {files.length > 0 && (
        <ul className="ei-file-list">
          {files.map((f, i) => (
            <li key={`${f.name}-${f.size}-${i}`}>
              <span className="ei-file-name" title={f.name}>{f.name}</span>
              <span className="ei-file-size">{formatBytes(f.size)}</span>
              <button
                type="button"
                className="ei-link-btn"
                onClick={() => onChange(files.filter((_, j) => j !== i))}
                disabled={disabled}
                aria-label={`Remove ${f.name}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function InitiativeAttachments({
  initiative,
  username,
  attachments = [],
  onChange,
  loading = false,
  failed = false,
}) {
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const initiativeId = initiative?.initiative_id;

  const upload = async () => {
    if (!picked.length) return;
    setBusy(true);
    setError(null);
    try {
      const next = await uploadInitiativeAttachments(
        initiativeId,
        username,
        picked,
        null
      );
      setPicked([]);
      onChange?.(next);
    } catch (err) {
      setError(initiativeErrorMessage(err, "Could not attach the files."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (attachment) => {
    setRemovingId(attachment.attachment_id);
    setError(null);
    try {
      const next = await deleteInitiativeAttachment(
        initiativeId,
        attachment.attachment_id,
        username
      );
      onChange?.(next);
    } catch (err) {
      setError(initiativeErrorMessage(err, "Could not remove the file."));
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="ei-attachments">
      <p className="ei-detail-label">
        <FieldHelp id="efficiencyInitiatives.supportingDocuments">Supporting documents</FieldHelp>{attachments.length > 0 ? ` (${attachments.length})` : ""}
      </p>

      {loading ? (
        <p className="ei-loading-row" role="status">
          <span className="ei-spinner" aria-hidden="true" />
          Loading documents…
        </p>
      ) : failed ? (
        // "No documents attached" would be a claim. The request failed, so there
        // is no claim to make.
        <p className="ei-hint">Documents could not be loaded.</p>
      ) : attachments.length === 0 ? (
        <p className="ei-hint">No documents attached.</p>
      ) : (
        <ul className="ei-attachment-list">
          {attachments.map((a) => (
            <li key={a.attachment_id}>
              <a
                className="ei-attachment-name"
                href={attachmentDownloadUrl(initiativeId, a.attachment_id, username)}
                target="_blank"
                rel="noreferrer"
                title={`${a.kind} · ${a.file_name}`}
              >
                {a.file_name}
              </a>
              <span className="ei-file-size">{formatBytes(a.size_bytes)}</span>
              <span className="ei-attachment-meta">
                {a.uploaded_by}
                {a.uploaded_at ? ` · ${new Date(a.uploaded_at).toLocaleDateString()}` : ""}
              </span>
              <button
                type="button"
                className="ei-link-btn danger"
                onClick={() => remove(a)}
                disabled={busy || removingId === a.attachment_id}
              >
                {removingId === a.attachment_id ? "Removing…" : "Remove"}
              </button>
            </li>
          ))}
        </ul>
      )}

      {!loading && !failed && (
        <FilePicker
          files={picked}
          onChange={setPicked}
          disabled={busy}
          inputId={`ei-detail-files-${initiativeId}`}
        />
      )}

      {picked.length > 0 && (
        <button
          type="button"
          className="ei-secondary-btn"
          onClick={upload}
          disabled={busy}
        >
          {busy ? "Attaching…" : `Attach ${picked.length} file(s)`}
        </button>
      )}

      {error && <p className="ei-file-error">{error}</p>}
    </div>
  );
}
